
import React, { memo, useCallback } from "react";
import { Movie, PendingAction } from "@/types/movie";
import UndoCardGrid from "./UndoCardGrid";
import UndoCardList from "./UndoCardList";
import { useUndoCardLogic } from "@/hooks/undoCard/useUndoCardLogic";

interface UndoCardProps {
  movie: Movie;
  action: PendingAction;
  onUndo: (actionId: string) => void;
  viewMode?: "grid" | "list";
}

// Using memo to prevent unnecessary re-renders with a custom equality function
const UndoCardWrapper = memo<UndoCardProps>(({ 
  movie, 
  action,
  onUndo,
  viewMode = "grid" 
}) => {
  const {
    progress,
    isExpiring,
    getActionText,
    handleUndo
  } = useUndoCardLogic(movie, action, onUndo);
  
  // Create memoized props-passing callbacks to prevent re-renders
  const getActionTextCallback = useCallback(() => getActionText(), [getActionText]);
  
  // Delegate to the appropriate view component based on viewMode
  return viewMode === "list" 
    ? <UndoCardList 
        movie={movie} 
        getActionText={getActionTextCallback} 
        handleUndo={handleUndo}
        progress={progress}
        isExpiring={isExpiring}
      />
    : <UndoCardGrid 
        movie={movie} 
        getActionText={getActionTextCallback} 
        handleUndo={handleUndo}
        progress={progress}
        isExpiring={isExpiring}
      />;
}, (prevProps, nextProps) => {
  // Custom equality function using TMDB ID for comparison - using camelCase tmdbId
  return (
    prevProps.action.id === nextProps.action.id &&
    prevProps.viewMode === nextProps.viewMode &&
    prevProps.movie.tmdbId === nextProps.movie.tmdbId // Fixed to use camelCase
  );
});

UndoCardWrapper.displayName = "UndoCardWrapper";

export default UndoCardWrapper;
